// @ts-nocheck
// src/pages/SchoolAttendanceAdmin.tsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import PageTemplate from "../components/PageTemplate";
import Spinner from "../components/Spinner";
import ErrorPage from "../components/ErrorPage";
import { useCan } from "../authz/useCan";
import { fetchSchoolYears } from "../utils/fetchSchoolYears";
import http from "../api/apiClient";
import {
  fetchAttendanceRecords,
  updateAttendanceRecord,
  upsertAttendanceRecord,
} from "../api/attendance";
import { parseToDate, formatDateDMY } from "../utils/dateUtils";
import { translateClassName } from "../utils/classroomUtils";

const DEFAULT_RANGE_DAYS = 7;

const formatIsoDate = (date) => {
  const d = parseToDate(date);
  if (!d) return "";
  return d.toISOString().slice(0, 10);
};

const getDefaultDateRange = () => {
  const today = new Date();
  const from = new Date(today);
  from.setDate(today.getDate() - DEFAULT_RANGE_DAYS);
  return {
    from: formatIsoDate(from),
    to: formatIsoDate(today),
  };
};

const statusOptions = (t) => [
  { value: "PRESENT", label: t("attendance.status.present", { defaultValue: "Aanwezig" }) },
  { value: "ABSENT", label: t("attendance.status.absent", { defaultValue: "Afwezig" }) },
  { value: "EXCUSED", label: t("attendance.status.excused", { defaultValue: "Geoorloofd afwezig" }) },
  { value: "LATE", label: t("attendance.status.late", { defaultValue: "Te laat" }) },
];

const normalizeId = (value) =>
  value == null ? "" : String(value).trim().toLowerCase();

const isNonEmptyString = (value) => typeof value === "string" && value.trim().length > 0;

const joinNames = (...parts) => parts.filter(isNonEmptyString).join(" ");

const extractNameFromEntity = (entity, visited = new Set(), depth = 0) => {
  if (!entity || typeof entity !== "object" || depth > 4) return null;
  if (visited.has(entity)) return null;
  visited.add(entity);

  const preferredKeys = [
    "preferredName",
    "nickname",
    "displayName",
    "fullName",
    "name",
    "childName",
    "studentName",
  ];

  for (const key of preferredKeys) {
    if (isNonEmptyString(entity[key])) {
      return entity[key].trim();
    }
  }

  const combos = [
    [entity.firstName, entity.middleName, entity.lastName],
    [entity.firstName, entity.lastName],
    [entity.givenName, entity.familyName],
    [entity.childFirstName, entity.childMiddleName, entity.childLastName],
    [entity.childFirstName, entity.childLastName],
    [entity.studentFirstName, entity.studentLastName],
    [entity.personFirstName, entity.personLastName],
  ];

  for (const combo of combos) {
    const joined = joinNames(...combo);
    if (joined) return joined;
  }

  const nestedKeys = [
    "child",
    "student",
    "person",
    "profile",
    "user",
    "member",
    "participant",
    "data",
    "info",
  ];

  for (const key of nestedKeys) {
    const nested = entity[key];
    if (Array.isArray(nested)) {
      for (const candidate of nested) {
        const nestedName = extractNameFromEntity(candidate, visited, depth + 1);
        if (nestedName) return nestedName;
      }
    } else {
      const nestedName = extractNameFromEntity(nested, visited, depth + 1);
      if (nestedName) return nestedName;
    }
  }

  for (const value of Object.values(entity)) {
    if (typeof value === "object" && value !== null) {
      const nestedName = extractNameFromEntity(value, visited, depth + 1);
      if (nestedName) return nestedName;
    }
  }

  return null;
};

const buildChildLabel = (record, childLookup) => {
  const resolveChildId = () =>
    record?.childId ??
    record?.child?.id ??
    record?.child?.childId ??
    record?.child?.child?.id ??
    record?.studentId ??
    record?.student?.id ??
    record?.student?.studentId ??
    null;

  const childId = resolveChildId();

  const lookupCandidate =
    childId != null
      ? childLookup?.get(String(childId).toLowerCase()) ??
        childLookup?.get(String(childId)) ??
        childLookup?.get(childId)
      : null;

  const candidateObjects = [];
  const seen = new Set();
  const addCandidate = (candidate) => {
    if (!candidate || typeof candidate !== "object") return;
    if (seen.has(candidate)) return;
    seen.add(candidate);
    candidateObjects.push(candidate);
  };

  addCandidate(record);
  addCandidate(record?.child);
  addCandidate(record?.student);
  addCandidate(record?.child?.child);
  addCandidate(record?.child?.student);
  addCandidate(record?.child?.person);
  addCandidate(record?.child?.child?.student);
  addCandidate(record?.child?.child?.person);
  addCandidate(record?.student?.child);
  addCandidate(record?.student?.person);
  addCandidate(lookupCandidate);
  addCandidate(lookupCandidate?.child);
  addCandidate(lookupCandidate?.student);
  addCandidate(lookupCandidate?.person);

  const directStrings = [
    record?.childName,
    record?.studentName,
    record?.studentDisplayName,
    record?.displayName,
    record?.fullName,
    joinNames(record?.firstName, record?.middleName, record?.lastName),
    joinNames(record?.childFirstName, record?.childMiddleName, record?.childLastName),
    joinNames(record?.studentFirstName, record?.studentLastName),
  ];

  for (const direct of directStrings) {
    if (isNonEmptyString(direct)) {
      return direct.trim();
    }
  }

  for (const candidate of candidateObjects) {
    const name = extractNameFromEntity(candidate);
    if (name) return name;
  }

  return childId != null ? String(childId) : "-";
};

const normalizeChildId = (child) => {
  if (!child) return null;
  return (
    child.id ??
    child.childId ??
    child.child?.id ??
    child.referenceId ??
    child.childReferenceId ??
    null
  );
};

const childDisplayName = (child) => {
  if (!child) return "-";
  const parts = [
    child.displayName,
    child.fullName,
    [child.firstName, child.middleName, child.lastName].filter(Boolean).join(" "),
    [child.firstName, child.lastName].filter(Boolean).join(" "),
    child.name,
  ];
  return parts.find((value) => value && value.trim().length > 0) ?? "-";
};

const mergeChildrenWithRecordsForEntry = (children = [], records = [], fallbackLookup = new Map()) => {
  const recordByChild = new Map();
  records.forEach((record) => {
    const candidates = [
      record?.childId,
      record?.child?.id,
      record?.child?.childId,
      record?.child?.child?.id,
      record?.studentId,
      record?.student?.id,
      record?.student?.studentId,
    ];
    for (const candidate of candidates) {
      if (candidate != null) {
        recordByChild.set(String(candidate), record);
        break;
      }
    }
  });

  const students = [];
  const seenIds = new Set();

  children.forEach((child) => {
    const childId = normalizeChildId(child);
    if (!childId) return;
    const record = recordByChild.get(String(childId));
    if (record) recordByChild.delete(String(childId));
    seenIds.add(String(childId));
    students.push({
      childId: childId,
      recordId: record?.id ?? record?.recordId ?? null,
      name: childDisplayName(child) || buildChildLabel(record, fallbackLookup),
      status: record?.status && record.status !== "X" ? record.status : "",
    });
  });

  recordByChild.forEach((record, childId) => {
    if (seenIds.has(childId)) return;
    students.push({
      childId,
      recordId: record?.id ?? record?.recordId ?? null,
      name:
        buildChildLabel(record, fallbackLookup) ||
        childDisplayName(record?.child) ||
        `#${childId}`,
      status: record?.status && record.status !== "X" ? record.status : "",
    });
  });

  return students.sort((a, b) =>
    (a.name || "").localeCompare(b.name || "", undefined, {
      numeric: true,
      sensitivity: "base",
    })
  );
};

const SchoolAttendanceAdmin = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const canManageAttendance = useCan("school.attendance", "manage");
  const canMarkAttendance = useCan("school.attendance", "manage");
  const canFillAttendance = canManageAttendance || canMarkAttendance;

  const defaultRange = useMemo(() => getDefaultDateRange(), []);
  const initialYearId = searchParams.get("yearId") || "";
  const initialClassroomId = searchParams.get("classroomId") || "";
  const initialDateParamRaw = searchParams.get("date") || "";
  const initialDateParam = /^\d{4}-\d{2}-\d{2}$/.test(initialDateParamRaw)
    ? initialDateParamRaw
    : "";

  const [yearsLoading, setYearsLoading] = useState(true);
  const [yearsError, setYearsError] = useState(null);
  const [schoolYears, setSchoolYears] = useState([]);
  const [selectedYearId, setSelectedYearId] = useState(initialYearId);

  const [classroomsLoading, setClassroomsLoading] = useState(false);
  const [classroomsError, setClassroomsError] = useState(null);
  const [classrooms, setClassrooms] = useState([]);
  const [selectedClassroomId, setSelectedClassroomId] = useState(initialClassroomId);

  const [dateRange, setDateRange] = useState(() => {
    if (initialDateParam) {
      return { from: initialDateParam, to: initialDateParam };
    }
    return { ...defaultRange };
  });
  const [attendanceEntryDate, setAttendanceEntryDate] = useState(
    () => initialDateParam || defaultRange.to
  );

  const [recordsLoading, setRecordsLoading] = useState(false);
  const [recordsError, setRecordsError] = useState(null);
  const [records, setRecords] = useState([]);
  const [classroomChildren, setClassroomChildren] = useState([]);
  const [dirtyMap, setDirtyMap] = useState({});
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [openStatusFor, setOpenStatusFor] = useState(null);
  const [showEntryOptions, setShowEntryOptions] = useState(false);
  const [entryStudents, setEntryStudents] = useState([]);
  const [entryLoading, setEntryLoading] = useState(false);
  const [entryError, setEntryError] = useState(null);
  const [entrySaving, setEntrySaving] = useState(false);
  const [entryMessage, setEntryMessage] = useState(null);

  const teacherRolePriority = useCallback((role) => {
    const normalized = String(role || "").toLowerCase();
    if (["primary", "lead", "mentor"].includes(normalized)) return 0;
    if (["assistant", "support"].includes(normalized)) return 1;
    return 2;
  }, []);

  const isKindergarten = useCallback((classroom) => {
    const type = String(classroom?.classType ?? classroom?.type ?? "").toLowerCase();
    if (type.includes("kinder") || type.includes("kleuter")) return true;
    const name = String(classroom?.className ?? classroom?.name ?? "").toLowerCase();
    return name.includes("kinder") || name.includes("kleuter");
  }, []);

  const getPrimaryTeacherName = useCallback(
    (classroom) => {
      if (!Array.isArray(classroom?.teachers) || classroom.teachers.length === 0) return "";
      const active = classroom.teachers.filter((teacher) => !teacher?.endDate);
      if (active.length === 0) return "";
      const sorted = active
        .slice()
        .sort(
          (a, b) =>
            teacherRolePriority(a?.role) - teacherRolePriority(b?.role)
        );
      const primary = sorted[0];
      if (!primary) return "";
      return (
        primary.teacherName ||
        [primary.firstName, primary.lastName].filter(Boolean).join(" ") ||
        primary.displayName ||
        primary.username ||
        primary.email ||
        String(primary.id ?? "")
      );
    },
    [teacherRolePriority]
  );

  const formatClassroomLabel = useCallback(
    (classroom) => {
      if (!classroom) return "";
      const baseLabel =
        translateClassName(classroom) ||
        classroom.name ||
        classroom.displayName ||
        classroom.className ||
        `#${classroom.id ?? ""}`;

      if (!isKindergarten(classroom)) {
        return baseLabel;
      }

      const teacherName = getPrimaryTeacherName(classroom);
      if (!teacherName) return baseLabel;
      return `${baseLabel} – ${teacherName}`;
    },
    [getPrimaryTeacherName, isKindergarten]
  );

  const sortClassrooms = useCallback(
    (list) => {
      const getStart = (cls) => parseToDate(cls?.startDate)?.getTime();
      const getLabel = (cls) => formatClassroomLabel(cls).toLowerCase();
      return list
        .slice()
        .sort((a, b) => {
          const aStart = getStart(a);
          const bStart = getStart(b);
          if (Number.isFinite(aStart) && Number.isFinite(bStart) && aStart !== bStart) {
            return aStart - bStart;
          }
          if (Number.isFinite(aStart) && !Number.isFinite(bStart)) return -1;
          if (!Number.isFinite(aStart) && Number.isFinite(bStart)) return 1;
          const labelCompare = getLabel(a).localeCompare(getLabel(b), undefined, {
            numeric: true,
            sensitivity: "base",
          });
          if (labelCompare !== 0) return labelCompare;
          return String(a.id ?? "").localeCompare(String(b.id ?? ""));
        });
    },
    [formatClassroomLabel]
  );

  const getRecordClassroomId = useCallback((record) => {
    if (!record || typeof record !== "object") return "";
    const candidates = [
      record.classroomId,
      record.classroom_id,
      record.classroomUUID,
      record.classroomUuid,
      record.classroom?.id,
      record.classroom?.uuid,
      record.classroom?.classroomId,
      record.classroom?.classroom?.id,
    ];
    for (const value of candidates) {
      const normalized = normalizeId(value);
      if (normalized) return normalized;
    }
    return "";
  }, []);

  const selectedClassroomIdNormalized = normalizeId(selectedClassroomId);

  const filteredRecords = useMemo(() => {
    if (!Array.isArray(records)) return [];
    if (!selectedClassroomIdNormalized) return records;
    return records.filter(
      (rec) => getRecordClassroomId(rec) === selectedClassroomIdNormalized
    );
  }, [records, selectedClassroomIdNormalized, getRecordClassroomId]);

  const loadEntryData = useCallback(
    async (classroomId, targetDate) => {
      if (!classroomId || !targetDate) {
        setEntryStudents([]);
        setEntryError(null);
        setEntryMessage(null);
        return;
      }

      setEntryLoading(true);
      setEntryError(null);
      setEntryMessage(null);
      try {
        const [childrenRes, recordsData] = await Promise.all([
          http.get(`/classrooms/${classroomId}/children`, {
            validateStatus: (status) => [200, 204, 404].includes(status),
          }),
          fetchAttendanceRecords({
            classroomId,
            date: targetDate,
          }),
        ]);

        const childrenList =
          childrenRes.status === 200 && Array.isArray(childrenRes.data)
            ? childrenRes.data
            : [];
        const normalizedClassId = normalizeId(classroomId);
        const recordsList = Array.isArray(recordsData)
          ? recordsData.filter(
              (record) => getRecordClassroomId(record) === normalizedClassId
            )
          : [];

        const fallbackLookup = new Map();
        childrenList.forEach((child) => {
          const childId = normalizeChildId(child);
          if (childId != null) {
            fallbackLookup.set(String(childId).toLowerCase(), child);
          }
        });

        const merged = mergeChildrenWithRecordsForEntry(childrenList, recordsList, fallbackLookup);
        setEntryStudents(merged);
        if (!merged.length) {
          setEntryMessage(
            t("attendance.admin.entry_no_students", {
              defaultValue: "Geen leerlingen gevonden voor deze klas op deze datum.",
            })
          );
        } else {
          setEntryMessage(null);
        }
      } catch (err) {
        setEntryStudents([]);
        setEntryError(
          err?.response?.status === 403
            ? t("error.403.message")
            : t("attendance.admin.entry_load_failed", {
                defaultValue: "Kon gegevens voor invullen niet laden.",
              })
        );
      } finally {
        setEntryLoading(false);
      }
    },
    [t]
  );

  useEffect(() => {
    setOpenStatusFor(null);
  }, [selectedClassroomId, dateRange.from, dateRange.to]);

  useEffect(() => {
    setAttendanceEntryDate((prev) => {
      if (!prev) {
        return dateRange.to || dateRange.from || "";
      }
      if (dateRange.from && prev < dateRange.from) {
        return dateRange.from;
      }
      if (dateRange.to && prev > dateRange.to) {
        return dateRange.to;
      }
      return prev;
    });
  }, [dateRange.from, dateRange.to]);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    let changed = false;
    const applyParam = (key, value) => {
      const current = next.get(key);
      if (value) {
        if (current !== value) {
          next.set(key, value);
          changed = true;
        }
      } else if (current !== null) {
        next.delete(key);
        changed = true;
      }
    };

    applyParam("yearId", selectedYearId);
    applyParam("classroomId", selectedClassroomId);
    applyParam("date", attendanceEntryDate);

    if (changed) {
      setSearchParams(next, { replace: true });
    }
  }, [
    attendanceEntryDate,
    searchParams,
    selectedClassroomId,
    selectedYearId,
    setSearchParams,
  ]);

  useEffect(() => {
    if (!showEntryOptions) return;
    if (!selectedClassroomId || !attendanceEntryDate) {
      setEntryStudents([]);
      setEntryMessage(
        t("attendance.admin.entry_select_class_hint", {
          defaultValue: "Selecteer een klas en datum om aanwezigheden in te vullen.",
        })
      );
      return;
    }
    loadEntryData(selectedClassroomId, attendanceEntryDate);
  }, [attendanceEntryDate, loadEntryData, selectedClassroomId, showEntryOptions, t]);

  const handleEntryStatusChange = useCallback((childId, status) => {
    setEntryError(null);
    setEntryStudents((prev) =>
      prev.map((student) =>
        String(student.childId) === String(childId)
          ? { ...student, status }
          : student
      )
    );
  }, []);

  const handleEntrySave = useCallback(async () => {
    if (!selectedClassroomId || !attendanceEntryDate) {
      setEntryError(
        t("attendance.admin.entry_select_class_hint", {
          defaultValue: "Selecteer een klas en datum om aanwezigheden in te vullen.",
        })
      );
      return;
    }

    const actionable = entryStudents.filter(
      (student) => student.status && String(student.status).trim().length > 0
    );
    if (actionable.length === 0) {
      setEntryError(
        t("attendance.admin.entry_no_changes", {
          defaultValue: "Geen wijzigingen om op te slaan.",
        })
      );
      return;
    }

    setEntrySaving(true);
    setEntryError(null);
    try {
      await Promise.all(
        actionable.map((student) => {
          const payload = {
            classroomId: selectedClassroomId,
            childId: student.childId,
            attendanceDate: attendanceEntryDate,
            status: student.status,
          };
          if (student.recordId) {
            return updateAttendanceRecord(student.recordId, payload);
          }
          return upsertAttendanceRecord(payload);
        })
      );

      const refreshed = await fetchAttendanceRecords({
        classroomId: selectedClassroomId,
        fromDate: dateRange.from,
        toDate: dateRange.to,
      });
      setRecords(Array.isArray(refreshed) ? refreshed : []);
      setFeedback({
        type: "success",
        message: t("attendance.admin.entry_saved", {
          defaultValue: "Aanwezigheden zijn bijgewerkt.",
        }),
      });
      setShowEntryOptions(false);
      setEntryStudents([]);
      setEntryMessage(null);
    } catch (err) {
      setEntryError(
        err?.response?.status === 403
          ? t("error.403.message")
          : t("attendance.admin.entry_save_failed", {
              defaultValue: "Opslaan van aanwezigheden is mislukt.",
            })
      );
    } finally {
      setEntrySaving(false);
    }
  }, [
    attendanceEntryDate,
    dateRange.from,
    dateRange.to,
    entryStudents,
    selectedClassroomId,
    setFeedback,
    t,
  ]);

  useEffect(() => {
    if (!canManageAttendance) return;
    const loadYears = async () => {
      setYearsLoading(true);
      setYearsError(null);
      try {
        const result = await fetchSchoolYears();
        const list = Array.isArray(result) ? result : result?.data || [];
        setSchoolYears(list);
        if (list.length > 0) {
          setSelectedYearId((prev) => {
            const normalizedPrev = prev ? String(prev) : "";
            if (
              normalizedPrev &&
              list.some((year) => String(year.id ?? "") === normalizedPrev)
            ) {
              return normalizedPrev;
            }
            const today = new Date();
            const current =
              list.find((year) => {
                const start = parseToDate(year.startDate);
                const end = parseToDate(year.endDate);
                if (!start || !end) return false;
                return start <= today && today <= end;
              }) ?? list[0];
            return String(current?.id ?? "");
          });
        } else {
          setSelectedYearId("");
        }
      } catch (err) {
        setYearsError(
          err?.response?.status === 403
            ? t("error.403.message")
            : t("attendance.admin.load_years_failed", { defaultValue: "Kon schooljaren niet laden." })
        );
      } finally {
        setYearsLoading(false);
      }
    };

    loadYears();
  }, [canManageAttendance, t]);

  useEffect(() => {
    if (!selectedYearId) {
      setClassrooms([]);
      setSelectedClassroomId("");
      return;
    }

    const loadClassrooms = async () => {
      setClassroomsLoading(true);
      setClassroomsError(null);
      try {
        const params =
          selectedYearId && String(selectedYearId).trim().length > 0
            ? { schoolYearId: selectedYearId }
            : {};
        const { data } = await http.get("/classrooms", { params });
        const list = Array.isArray(data) ? data : [];
        const normalizedSelectedYearId = String(selectedYearId || "");
        const selectedYear = schoolYears.find(
          (year) => String(year.id ?? "") === normalizedSelectedYearId
        );
        const filteredList = normalizedSelectedYearId
          ? list.filter((classroom) => {
              const candidateIds = [
                classroom?.schoolYearId,
                classroom?.schoolYearID,
                classroom?.school_year_id,
                classroom?.schoolYear?.id,
                classroom?.schoolYear?.schoolYearId,
                classroom?.schoolYear?.yearId,
                classroom?.schoolYear?.uuid,
              ]
                .map((value) => (value != null ? String(value) : null))
                .filter(Boolean);
              if (candidateIds.includes(normalizedSelectedYearId)) {
                return true;
              }

              if (!selectedYear) return false;

              const yearStart = parseToDate(selectedYear.startDate);
              const yearEnd = parseToDate(selectedYear.endDate);
              const classStart = parseToDate(classroom?.startDate);
              const classEnd = parseToDate(classroom?.endDate);

              if (classStart && yearStart && classStart < yearStart && !classEnd) {
                return false;
              }

              const overlaps = (() => {
                if (!yearStart && !yearEnd) return false;
                const start = classStart ?? classEnd;
                const end = classEnd ?? classStart;
                if (!start && !end) return false;

                const inRange = (date) => {
                  if (!date) return false;
                  if (yearStart && date < yearStart) return false;
                  if (yearEnd && date > yearEnd) return false;
                  return true;
                };

                if (inRange(start) || inRange(end)) return true;
                if (yearStart && yearEnd && start && end) {
                  return start <= yearEnd && end >= yearStart;
                }
                return false;
              })();

              return overlaps;
            })
          : list;

        const sorted = sortClassrooms(filteredList);
        setClassrooms(sorted);
        setSelectedClassroomId((prev) => {
          if (!prev) return "";
          const exists = sorted.some(
            (classroom) => String(classroom.id ?? "") === String(prev)
          );
          return exists ? prev : "";
        });
      } catch (err) {
        setClassroomsError(
          err?.response?.status === 403
            ? t("error.403.message")
            : t("attendance.admin.load_classrooms_failed", { defaultValue: "Kon klassen niet laden." })
        );
        setClassrooms([]);
        setSelectedClassroomId("");
      } finally {
        setClassroomsLoading(false);
      }
    };

    loadClassrooms();
  }, [selectedYearId, t, schoolYears, sortClassrooms]);

  useEffect(() => {
    if (!selectedClassroomId) {
      setRecords([]);
      setClassroomChildren([]);
      return;
    }

    let cancel = false;

    const loadRecords = async () => {
      setRecordsLoading(true);
      setRecordsError(null);
      setFeedback(null);
      try {
        let childrenList = [];
        try {
          const childrenRes = await http.get(`/classrooms/${selectedClassroomId}/children`, {
            validateStatus: (status) => [200, 204, 404].includes(status),
          });
          childrenList =
            childrenRes.status === 200 && Array.isArray(childrenRes.data)
              ? childrenRes.data
              : [];
        } catch (childErr) {
          childrenList = [];
        }
        if (!cancel) {
          setClassroomChildren(childrenList);
        }

        const recordsData = await fetchAttendanceRecords({
          classroomId: selectedClassroomId,
          fromDate: dateRange.from,
          toDate: dateRange.to,
        });
        if (!cancel) {
          setRecords(Array.isArray(recordsData) ? recordsData : []);
          setDirtyMap({});
        }
      } catch (err) {
        if (!cancel) {
          setRecordsError(
            err?.response?.status === 403
              ? t("error.403.message")
              : t("attendance.admin.load_records_failed", { defaultValue: "Kon aanwezigheden niet laden." })
          );
          setClassroomChildren([]);
        }
      } finally {
        if (!cancel) {
          setRecordsLoading(false);
        }
      }
    };

    loadRecords();

    return () => {
      cancel = true;
    };
  }, [selectedClassroomId, dateRange.from, dateRange.to, t]);

  const groupedRecords = useMemo(() => {
    if (!Array.isArray(filteredRecords)) return [];
    const map = new Map();
    filteredRecords.forEach((rec) => {
      const rawDate = rec.date || rec.attendanceDate || rec.day;
      const normalized = formatIsoDate(rawDate);
      const key = normalized || (rawDate ? String(rawDate) : "unknown");
      if (!map.has(key)) {
        map.set(key, { key, normalized, rawDate, entries: [] });
      }
      map.get(key).entries.push(rec);
    });
    return Array.from(map.values()).sort((a, b) => {
      const aKey = a.normalized || a.key;
      const bKey = b.normalized || b.key;
      return aKey > bKey ? -1 : aKey < bKey ? 1 : 0;
    });
  }, [filteredRecords]);

  const childLookup = useMemo(() => {
    const map = new Map();
    (Array.isArray(classroomChildren) ? classroomChildren : []).forEach((child) => {
      const process = (candidate) => {
        if (!candidate || typeof candidate !== "object") return;
        const rawId =
          candidate.id ??
          candidate.childId ??
          candidate.child?.id ??
          candidate.child?.childId ??
          null;
        if (rawId != null) {
          const key = String(rawId).toLowerCase();
          if (key && !map.has(key)) {
            map.set(key, candidate);
          }
        }
      };
      process(child);
      process(child?.child);
    });
    return map;
  }, [classroomChildren]);

  const statusChoices = useMemo(() => statusOptions(t), [t]);
  const canSaveEntries = useMemo(
    () =>
      entryStudents.some(
        (student) => student.status && String(student.status).trim().length > 0
      ),
    [entryStudents]
  );
  const statusColor = useCallback((status) => {
    switch (String(status || "").toUpperCase()) {
      case "PRESENT":
        return "border-green-300 bg-green-100 text-green-700 dark:border-green-500/60 dark:bg-green-500/10 dark:text-green-200";
      case "ABSENT":
        return "border-red-300 bg-red-100 text-red-700 dark:border-red-500/60 dark:bg-red-500/10 dark:text-red-200";
      case "EXCUSED":
        return "border-yellow-300 bg-yellow-100 text-yellow-700 dark:border-yellow-500/60 dark:bg-yellow-500/10 dark:text-yellow-200";
      case "LATE":
        return "border-purple-300 bg-purple-100 text-purple-700 dark:border-purple-500/60 dark:bg-purple-500/10 dark:text-purple-200";
      default:
        return "border-gray-300 bg-gray-100 text-gray-600 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200";
    }
  }, []);

  const handleStatusChange = (recordId, newStatus) => {
    setRecords((prev) =>
      prev.map((rec) =>
        String(rec.id ?? rec.recordId) === String(recordId) ? { ...rec, status: newStatus } : rec
      )
    );
    setDirtyMap((prev) => ({
      ...prev,
      [recordId]: {
        ...(prev[recordId] || {}),
        status: newStatus,
      },
    }));
    setOpenStatusFor(null);
  };

  const handleSave = async () => {
    if (saving || !dirtyMap || Object.keys(dirtyMap).length === 0) return;
    setSaving(true);
    setFeedback(null);
    try {
      const entries = Object.entries(dirtyMap);
      await Promise.all(
        entries.map(([recordId, updates]) => {
          const record =
            records.find(
              (rec) => String(rec.id ?? rec.recordId) === String(recordId)
            ) || {};
          const normalizeStatus = (value) => (value == null || value === "" ? "X" : value);
          const payload = {
            childId: updates.childId ?? record?.childId ?? record?.child?.id,
            classroomId:
              updates.classroomId ?? record?.classroomId ?? record?.classroom?.id,
            attendanceDate:
              updates.attendanceDate ??
              updates.date ??
              record?.attendanceDate ??
              record?.date ??
              record?.day,
            status: normalizeStatus(
              updates.status ?? record?.status ?? record?.attendanceStatus
            ),
          };
          return updateAttendanceRecord(recordId, payload);
        })
      );
      setDirtyMap({});
      setFeedback({
        type: "success",
        message: t("attendance.admin.save_success", { defaultValue: "Aanwezigheden bijgewerkt." }),
      });
    } catch (err) {
      setFeedback({
        type: "error",
        message:
          err?.response?.status === 403
            ? t("error.403.message")
            : t("attendance.admin.save_failed", { defaultValue: "Opslaan is mislukt." }),
      });
    } finally {
      setSaving(false);
    }
  };

  const toggleStatusMenu = (recordId) => {
    setOpenStatusFor((prev) => (prev === recordId ? null : recordId));
  };

  if (!canManageAttendance) {
    return (
      <ErrorPage
        status={403}
        message={t("error.403.message")}
        onRetry={() => window.location.reload()}
      />
    );
  }

  if (yearsLoading) {
    return <Spinner />;
  }

  if (yearsError) {
    return (
      <ErrorPage
        message={yearsError}
        onRetry={() => window.location.reload()}
      />
    );
  }

  const isBusy = classroomsLoading || recordsLoading || saving;

  return (
    <PageTemplate
      title={t("attendance.admin.title", { defaultValue: "Aanwezigheden beheren" })}
      buttonLabel={
        dirtyMap && Object.keys(dirtyMap).length > 0
          ? saving
            ? t("attendance.admin.saving", { defaultValue: "Opslaan…" })
            : t("attendance.admin.save_button", { defaultValue: "Wijzigingen opslaan" })
          : null
      }
      onButtonClick={dirtyMap && Object.keys(dirtyMap).length > 0 ? handleSave : undefined}
    >
      <div className="space-y-6">
        <div className="rounded-3xl border border-indigo-100/70 bg-gradient-to-br from-indigo-50/70 via-white to-white p-4 shadow-sm dark:border-indigo-500/30 dark:bg-gradient-to-br dark:from-gray-900 dark:via-gray-900/95 dark:to-gray-800 sm:p-5">
          {canFillAttendance && (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setShowEntryOptions((prev) => !prev)}
                aria-expanded={showEntryOptions}
                className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-indigo-600 to-indigo-500 px-4 py-2 text-sm font-semibold text-white shadow-[0_12px_30px_rgba(79,70,229,0.25)] transition hover:-translate-y-0.5 hover:shadow-[0_18px_40px_rgba(79,70,229,0.35)] focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900"
              >
                {t("attendance.admin.fill_attendance_button", {
                  defaultValue: "Aanwezigheid invullen",
                })}
              </button>
            </div>
          )}

          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
            <label className="flex flex-col space-y-2">
              <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                {t("attendance.admin.school_year", { defaultValue: "Schooljaar" })}
              </span>
              <select
                className="rounded-xl border border-indigo-100/70 bg-white/90 px-3 py-2 text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 dark:border-indigo-500/30 dark:bg-gray-900/80 dark:text-gray-100"
                value={selectedYearId}
                onChange={(e) => setSelectedYearId(e.target.value)}
              >
                {schoolYears.map((year) => (
                  <option key={year.id ?? year.__id ?? year.label} value={year.id ?? ""}>
                    {year.label || `${formatDateDMY(year.startDate)} – ${formatDateDMY(year.endDate)}`}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex flex-col space-y-2">
              <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                {t("attendance.admin.classroom", { defaultValue: "Klas" })}
              </span>
              {classroomsLoading ? (
                <div className="py-2 text-sm text-gray-500 dark:text-gray-400">
                  {t("attendance.admin.loading_classrooms", { defaultValue: "Klassen laden…" })}
                </div>
              ) : (
                <select
                  className="rounded-xl border border-indigo-100/70 bg-white/90 px-3 py-2 text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 dark:border-indigo-500/30 dark:bg-gray-900/80 dark:text-gray-100"
                  value={selectedClassroomId}
                  onChange={(e) => setSelectedClassroomId(e.target.value)}
                >
                  {classrooms.length === 0 ? (
                    <option value="">
                      {t("attendance.admin.no_classrooms", { defaultValue: "Geen klassen" })}
                    </option>
                  ) : (
                    <>
                      <option value="">
                        {t("attendance.admin.select_classroom", {
                          defaultValue: "Selecteer een klas om aanwezigheden te bekijken.",
                        })}
                      </option>
                      {classrooms.map((classroom) => (
                        <option key={classroom.id} value={classroom.id}>
                          {formatClassroomLabel(classroom)}
                        </option>
                      ))}
                    </>
                  )}
                </select>
              )}
              {classroomsError && (
                <span className="text-sm text-red-600 dark:text-red-400">{classroomsError}</span>
              )}
            </label>

            <div className="grid grid-cols-2 gap-4">
              <label className="flex flex-col space-y-2">
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {t("attendance.admin.from_date", { defaultValue: "Vanaf" })}
                </span>
                <input
                  type="date"
                  className="rounded-xl border border-indigo-100/70 bg-white/90 px-3 py-2 text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 dark:border-indigo-500/30 dark:bg-gray-900/80 dark:text-gray-100"
                  value={dateRange.from}
                  max={dateRange.to || undefined}
                  disabled={!selectedClassroomId}
                  onChange={(e) =>
                    setDateRange((prev) => ({ ...prev, from: e.target.value }))
                  }
                />
              </label>
              <label className="flex flex-col space-y-2">
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {t("attendance.admin.to_date", { defaultValue: "Tot en met" })}
                </span>
                <input
                  type="date"
                  className="rounded-xl border border-indigo-100/70 bg-white/90 px-3 py-2 text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 dark:border-indigo-500/30 dark:bg-gray-900/80 dark:text-gray-100"
                  value={dateRange.to}
                  min={dateRange.from || undefined}
                  disabled={!selectedClassroomId}
                  onChange={(e) =>
                    setDateRange((prev) => ({ ...prev, to: e.target.value }))
                  }
                />
              </label>
            </div>
          </div>

          {canFillAttendance && showEntryOptions && (
            <div className="rounded-3xl border border-indigo-100/70 bg-gradient-to-br from-white via-indigo-50/70 to-white p-4 shadow-sm dark:border-indigo-500/30 dark:bg-gradient-to-br dark:from-gray-900 dark:via-gray-900/95 dark:to-gray-800 sm:p-5">
              <div className="flex flex-col gap-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <label className="flex flex-col space-y-1">
                    <span className="text-xs font-semibold uppercase tracking-wide text-indigo-900 dark:text-indigo-100">
                      {t("attendance.admin.entry_date_label", {
                        defaultValue: "Datum om in te vullen",
                      })}
                    </span>
                    <input
                      type="date"
                      className="rounded-xl border border-indigo-100/70 bg-white/90 px-3 py-2 text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 dark:border-indigo-500/30 dark:bg-gray-900/80 dark:text-gray-100"
                      value={attendanceEntryDate || ""}
                      onChange={(e) => setAttendanceEntryDate(e.target.value)}
                    />
                  </label>

                  <label className="flex flex-col space-y-1">
                    <span className="text-xs font-semibold uppercase tracking-wide text-indigo-900 dark:text-indigo-100">
                      {t("attendance.admin.entry_class_label", {
                        defaultValue: "Klas kiezen",
                      })}
                    </span>
                    {classroomsLoading ? (
                      <div className="rounded-xl border border-dashed border-indigo-200/80 px-3 py-2 text-sm text-gray-500 dark:border-indigo-500/40 dark:text-gray-300">
                        {t("attendance.admin.loading_classrooms", { defaultValue: "Klassen laden…" })}
                      </div>
                    ) : (
                      <select
                        className="rounded-xl border border-indigo-100/70 bg-white/90 px-3 py-2 text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 dark:border-indigo-500/30 dark:bg-gray-900/80 dark:text-gray-100"
                        value={selectedClassroomId}
                        onChange={(e) => setSelectedClassroomId(e.target.value)}
                      >
                        <option value="">
                          {t("attendance.admin.select_classroom", {
                            defaultValue: "Selecteer een klas.",
                          })}
                        </option>
                        {classrooms.map((classroom) => (
                          <option key={classroom.id} value={classroom.id}>
                            {formatClassroomLabel(classroom)}
                          </option>
                        ))}
                      </select>
                    )}
                  </label>
                </div>

                {entryError && (
                  <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-500/60 dark:bg-rose-500/10 dark:text-rose-200">
                    {entryError}
                  </div>
                )}
                {entryMessage && !entryError && (
                  <div className="rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700 dark:border-blue-500/50 dark:bg-blue-500/10 dark:text-blue-100">
                    {entryMessage}
                  </div>
                )}

                <div className="rounded-3xl border border-white/70 bg-white/90 shadow-sm dark:border-gray-700/60 dark:bg-gray-900/80">
                  {entryLoading ? (
                    <div className="flex justify-center py-6">
                      <Spinner />
                    </div>
                  ) : entryStudents.length > 0 ? (
                    <div className="divide-y divide-indigo-100 dark:divide-gray-800">
                      {entryStudents.map((student) => (
                        <div
                          key={student.childId}
                          className="flex flex-col gap-3 px-4 py-3 md:flex-row md:items-center md:justify-between"
                        >
                          <div>
                            <p className="text-sm font-semibold text-indigo-900 dark:text-indigo-100">
                              {student.name}
                            </p>
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            {statusChoices.map((option) => {
                              const isActive = student.status === option.value;
                              return (
                                <button
                                  key={option.value}
                                  type="button"
                                  onClick={() => handleEntryStatusChange(student.childId, option.value)}
                                  disabled={entrySaving}
                                  className={[
                                    "rounded px-3 py-1 text-xs font-semibold transition",
                                    isActive
                                      ? "bg-indigo-600 text-white"
                                      : "bg-indigo-100 text-indigo-800 hover:bg-indigo-200 dark:bg-indigo-800/40 dark:text-indigo-100 dark:hover:bg-indigo-700/60",
                                  ].join(" ")}
                                >
                                  {option.label}
                                </button>
                              );
                            })}
                            <button
                              type="button"
                              onClick={() => handleEntryStatusChange(student.childId, "")}
                              disabled={entrySaving}
                              className="rounded px-3 py-1 text-xs font-semibold text-gray-600 transition hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-100"
                            >
                              {t("attendance.admin.entry_clear", { defaultValue: "Geen markering" })}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    !entryLoading && !entryError && (
                      <div className="px-4 py-6 text-sm text-indigo-900 dark:text-indigo-100">
                        {t("attendance.admin.entry_no_students", {
                          defaultValue: "Geen leerlingen gevonden voor deze klas op deze datum.",
                        })}
                      </div>
                    )
                  )}
                </div>

                <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                  <p className="text-xs text-gray-700 dark:text-gray-300">
                    {t("attendance.admin.entry_hint", {
                      defaultValue: "Selecteer een status per leerling en sla daarna op.",
                    })}
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setShowEntryOptions(false);
                        setEntryError(null);
                        setEntryMessage(null);
                      }}
                      className="rounded-xl border border-transparent px-4 py-2 text-sm font-semibold text-indigo-700 transition hover:-translate-y-0.5 hover:text-indigo-900 dark:text-indigo-200 dark:hover:text-indigo-100"
                    >
                      {t("common.cancel", { defaultValue: "Annuleren" })}
                    </button>
                    <button
                      type="button"
                      onClick={handleEntrySave}
                      disabled={
                        entrySaving ||
                        entryLoading ||
                        !selectedClassroomId ||
                        !attendanceEntryDate ||
                        !canSaveEntries
                      }
                      className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-indigo-600 to-indigo-500 px-4 py-2 text-sm font-semibold text-white shadow-[0_12px_30px_rgba(79,70,229,0.25)] transition hover:-translate-y-0.5 hover:shadow-[0_18px_40px_rgba(79,70,229,0.35)] focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {entrySaving
                        ? t("attendance.admin.entry_saving", { defaultValue: "Opslaan…" })
                        : t("attendance.admin.entry_save", { defaultValue: "Aanwezigheden opslaan" })}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {feedback && (
          <div
            className={`rounded-2xl border px-4 py-3 ${
              feedback.type === "success"
                ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/60 dark:bg-emerald-500/10 dark:text-emerald-200"
                : "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-500/60 dark:bg-rose-500/10 dark:text-rose-200"
            }`}
          >
            {feedback.message}
          </div>
        )}

        {recordsLoading ? (
          <Spinner />
        ) : recordsError ? (
          <ErrorPage
            message={recordsError}
            onRetry={() => setDateRange((prev) => ({ ...prev }))}
          />
        ) : selectedClassroomId ? (
          groupedRecords.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-indigo-100/70 px-4 py-6 text-center text-sm text-gray-500 dark:border-indigo-500/40 dark:text-gray-300">
              {t("attendance.admin.no_records", { defaultValue: "Geen aanwezigheden gevonden voor deze periode." })}
            </div>
          ) : (
            <div className="space-y-8">
              {groupedRecords.map(({ key, normalized, rawDate, entries }) => (
                <div
                  key={key}
                  className="space-y-4 rounded-3xl border border-indigo-100/70 bg-gradient-to-br from-white via-slate-50 to-white p-4 shadow-sm dark:border-indigo-500/30 dark:bg-gradient-to-br dark:from-gray-900 dark:via-gray-900/95 dark:to-gray-800 sm:p-5"
                >
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-indigo-700 dark:text-indigo-300">
                      {parseToDate(normalized || rawDate)?.toLocaleDateString() || normalized || rawDate || "—"}
                    </h2>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {t("attendance.admin.total_count", {
                        defaultValue: "{{count}} leerlingen",
                        count: entries.length,
                      })}
                    </span>
                  </div>
                  <div className="overflow-x-auto rounded-2xl border border-white/80 dark:border-gray-700/60">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="indigo-table-head text-xs font-semibold uppercase tracking-wider">
                        <tr>
                          <th className="px-4 py-3 text-left">
                            {t("attendance.admin.student", { defaultValue: "Leerling" })}
                          </th>
                          <th className="px-4 py-3 text-left">
                            {t("attendance.admin.status", { defaultValue: "Status" })}
                          </th>
                          <th className="px-4 py-3 text-left">
                            {t("attendance.admin.updated_at", { defaultValue: "Bijgewerkt" })}
                          </th>
                          <th className="px-4 py-3 text-left">
                            {t("attendance.admin.updated_by", { defaultValue: "Door" })}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white/95 text-gray-900 dark:divide-gray-800 dark:bg-gray-900/80 dark:text-gray-100">
                        {entries.map((record) => {
                          const recordId = record.id ?? record.recordId;
                          const isDirty = !!dirtyMap[recordId];
                          return (
                            <tr key={recordId} className={isDirty ? "bg-indigo-50 dark:bg-indigo-500/10" : ""}>
                              <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                {buildChildLabel(record, childLookup)}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                <div className="relative inline-block">
                                  <button
                                    type="button"
                                    className={`flex items-center gap-2 rounded border px-3 py-1 text-sm font-semibold transition hover:ring-2 hover:ring-indigo-200 dark:hover:ring-indigo-400 ${statusColor(record.status)}`}
                                    onClick={() => toggleStatusMenu(recordId)}
                                  >
                                    <span>
                                      {statusChoices.find((opt) => opt.value === record.status)?.label ||
                                        t("attendance.admin.status_unknown", { defaultValue: "Onbekend" })}
                                    </span>
                                    <svg
                                      className="h-4 w-4 text-current"
                                      viewBox="0 0 20 20"
                                      fill="currentColor"
                                      aria-hidden="true"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  </button>
                                  {openStatusFor === recordId && (
                                    <div className="absolute left-0 z-20 mt-2 w-48 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-900 dark:shadow-black/50">
                                      {statusChoices.map((option) => {
                                        const active = option.value === record.status;
                                        return (
                                          <button
                                            type="button"
                                            key={option.value}
                                            className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm transition hover:bg-indigo-50 dark:hover:bg-indigo-500/10 ${
                                              active
                                                ? "font-semibold text-indigo-600 dark:text-indigo-300"
                                                : "text-gray-700 dark:text-gray-200"
                                            }`}
                                            onClick={() => handleStatusChange(recordId, option.value)}
                                          >
                                            <span>{option.label}</span>
                                            {active && (
                                              <span className="text-xs uppercase text-indigo-400 dark:text-indigo-200">
                                                {t("attendance.today.reason_selected", {
                                                  defaultValue: "Gekozen",
                                                })}
                                              </span>
                                            )}
                                          </button>
                                        );
                                      })}
                                      <button
                                        type="button"
                                        className="w-full px-3 py-2 text-left text-sm text-gray-600 transition hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800"
                                        onClick={() => handleStatusChange(recordId, "")}
                                      >
                                        {t("attendance.today.clear_reason", {
                                          defaultValue: "Zet op niet gekozen",
                                        })}
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-200">
                                {record.updatedAt
                                  ? parseToDate(record.updatedAt)?.toLocaleString()
                                  : "—"}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-200">
                                {record.updatedByDisplayName ||
                                  record.updatedByUserId ||
                                  record.updatedBy ||
                                  record.updatedByName ||
                                  "—"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          <div className="rounded border border-dashed px-4 py-6 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
            {t("attendance.admin.select_classroom", { defaultValue: "Selecteer een klas om aanwezigheden te bekijken." })}
          </div>
        )}
      </div>
    </PageTemplate>
  );
};

export default SchoolAttendanceAdmin;
